import { lazy, ReactNode, Suspense } from "react";

import { LoadingOverlay } from "@mantine/core";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { useAuth } from "@/Components/Auth/AuthProvider";
import MainLayout from "@/Components/Layout/MainLayout";
import { Config } from "@/config/Config";

const LoginPage = lazy(() => import("@/Components/Auth/LoginPage"));
const AccountConfirmationPage = lazy(
	() => import("@/Components/Auth/AccountConfirmationPage")
);
const ForgotPasswordPage = lazy(
	() => import("@/Components/Auth/ForgotPasswordPage")
);
const ResetPasswordPage = lazy(
	() => import("@/Components/Auth/ResetPasswordPage")
);
const DashboardPage = lazy(
	() => import("@/Components/Dashboard/DashboardPage")
);
const ScenePage = lazy(() => import("@/Components/Scene/ScenePage"));
const PlaylistEditorPage = lazy(
	() => import("@/Components/Playlist/PlaylistEditorPage")
);
const StatisticsPage = lazy(
	() => import("@/Components/Statistics/StatisticsPage")
);
const UserManagePage = lazy(() => import("@/Components/Admin/UserManagePage"));
const BetaAdminPage = lazy(() => import("@/Components/Beta/BetaAdminPage"));
const BetaFormPage = lazy(() => import("@/Components/Beta/BetaFormPage"));
const PrivacyPage = lazy(() => import("@/Components/Static/PrivacyPage"));
const HelpPage = lazy(() => import("@/Components/Static/HelpPage"));
const MaintenancePage = lazy(
	() => import("@/Components/Maintenance/MaintenancePage")
);

export function isLoginPath(pathname: string): boolean {
	return pathname.startsWith("/login");
}

// /maintenance must stay public, or the maintenance-mode redirect would loop.
export function isPublicPath(pathname: string): boolean {
	return (
		pathname.startsWith("/privacy") ||
		pathname.startsWith("/help") ||
		pathname.startsWith("/beta/") ||
		pathname.startsWith("/forgot-password") ||
		pathname.startsWith("/maintenance")
	);
}

// Maintenance/auth route gates, re-evaluated on every navigation.
function Gates({ children }: { children: ReactNode }) {
	const location = useLocation();
	const { isAuthenticated } = useAuth();

	if (Config.maintenance_mode && !isPublicPath(location.pathname)) {
		return <Navigate to="/maintenance" replace />;
	}
	if (
		!isAuthenticated &&
		!isLoginPath(location.pathname) &&
		!isPublicPath(location.pathname)
	) {
		return <Navigate to="/login" replace />;
	}
	return children;
}

function RequireAdmin({ children }: { children: ReactNode }) {
	const { isAdmin } = useAuth();
	if (!isAdmin) {
		return <Navigate to="/scenes" replace />;
	}
	return children;
}

export default function Router() {
	return (
		<MainLayout>
			<Suspense fallback={<LoadingOverlay visible />}>
				<Gates>
					<Routes>
						<Route path="/login" element={<LoginPage />} />
						<Route
							path="/login/confirm/:uuid"
							element={<AccountConfirmationPage />}
						/>
						<Route
							path="/forgot-password"
							element={<ForgotPasswordPage />}
						/>
						<Route
							path="/login/reset-password"
							element={<ResetPasswordPage />}
						/>
						<Route
							path="/signup"
							element={<Navigate to="/login" replace />}
						/>
						<Route
							path="/dashboard"
							element={<Navigate to="/scenes" replace />}
						/>
						<Route path="/scenes" element={<DashboardPage />} />
						<Route path="/musics" element={<DashboardPage />} />
						<Route path="/playlists" element={<DashboardPage />} />
						<Route path="/scene/:id" element={<ScenePage />} />
						<Route
							path="/playlist/:id"
							element={<PlaylistEditorPage />}
						/>
						<Route
							path="/statistics"
							element={<StatisticsPage />}
						/>
						<Route
							path="/admin/users/manage"
							element={
								<RequireAdmin>
									<UserManagePage />
								</RequireAdmin>
							}
						/>
						<Route
							path="/admin/beta-registration"
							element={
								<RequireAdmin>
									<BetaAdminPage />
								</RequireAdmin>
							}
						/>
						<Route path="/beta/:token" element={<BetaFormPage />} />
						<Route path="/privacy" element={<PrivacyPage />} />
						<Route path="/help" element={<HelpPage />} />
						<Route
							path="/maintenance"
							element={<MaintenancePage />}
						/>
						<Route
							path="*"
							element={<Navigate to="/scenes" replace />}
						/>
					</Routes>
				</Gates>
			</Suspense>
		</MainLayout>
	);
}
